import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { accountingService } from '../../services/accountingService';
import { formatCurrency } from '../../utils/formatters';
import toast from 'react-hot-toast';
import {
  PlusIcon, PencilIcon, TrashIcon, ChevronRightIcon,
  ChevronDownIcon, FolderIcon, DocumentTextIcon
} from '@heroicons/react/24/outline';

const ChartOfAccounts = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [formData, setFormData] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    parent_id: '',
    opening_balance: '',
    description: ''
  });

  // Fetch chart of accounts
  const { data, isLoading, error } = useQuery(
    'chart-of-accounts',
    () => accountingService.getChartOfAccounts()
  );

  const createMutation = useMutation(
    (accountData) => accountingService.createLedgerAccount(accountData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chart-of-accounts');
        setShowModal(false);
        resetForm();
        toast.success('Account created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Creation failed');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => accountingService.updateLedgerAccount(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chart-of-accounts');
        setShowModal(false);
        resetForm();
        toast.success('Account updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Update failed');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => accountingService.deleteLedgerAccount(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chart-of-accounts');
        toast.success('Account deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Delete failed');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      account_code: '',
      account_name: '',
      account_type: 'asset',
      parent_id: '',
      opening_balance: '',
      description: ''
    });
    setEditingAccount(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      opening_balance: parseFloat(formData.opening_balance) || 0,
      parent_id: formData.parent_id ? parseInt(formData.parent_id) : null
    };

    if (editingAccount) {
      updateMutation.mutate({ id: editingAccount.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      parent_id: account.parent_id || '',
      opening_balance: account.opening_balance,
      description: account.description || ''
    });
    setShowModal(true);
  };

  const toggleExpand = (id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const accountTypes = {
    'asset': 'Assets',
    'liability': 'Liabilities',
    'equity': 'Equity',
    'income': 'Income',
    'expense': 'Expenses'
  };

  // Safely extract data from response
  const responseData = data?.data || {};
  const hierarchy = responseData.hierarchy || [];
  const totals = responseData.totals || {};
  const accounts = responseData.accounts || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading chart of accounts. Please try again later.
          <pre className="text-xs mt-2">{error.message}</pre>
        </div>
      </div>
    );
  }

  const renderAccountTree = (accountsList, level = 0) => {
    if (!accountsList || accountsList.length === 0) return null;

    return accountsList.map(account => (
      <React.Fragment key={account.id}>
        <tr className="hover:bg-gray-50">
          <td className="px-6 py-4 whitespace-nowrap">
            <div style={{ paddingLeft: `${level * 20}px` }} className="flex items-center">
              {account.children && account.children.length > 0 && (
                <button onClick={() => toggleExpand(account.id)} className="mr-1">
                  {expandedNodes[account.id] ?
                    <ChevronDownIcon className="h-4 w-4" /> :
                    <ChevronRightIcon className="h-4 w-4" />
                  }
                </button>
              )}
              {!account.children || account.children.length === 0 ? (
                <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
              ) : (
                <FolderIcon className="h-4 w-4 text-yellow-500 mr-2" />
              )}
              <span className="font-mono text-sm">{account.account_code}</span>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {account.account_name}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
            {accountTypes[account.account_type] || account.account_type}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
            {formatCurrency(account.opening_balance || 0)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-blue-600">
            {formatCurrency(account.current_balance || 0)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-center">
            <span className={`px-2 py-1 text-xs rounded-full ${account.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {account.is_active ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
            <button
              onClick={() => handleEdit(account)}
              className="text-blue-600 hover:text-blue-800"
            >
              <PencilIcon className="h-5 w-5 inline" />
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete account "${account.account_name}"? This may affect existing transactions.`)) {
                  deleteMutation.mutate(account.id);
                }
              }}
              className="text-red-600 hover:text-red-800"
            >
              <TrashIcon className="h-5 w-5 inline" />
            </button>
           </td>
        </tr>
        {expandedNodes[account.id] && account.children && account.children.length > 0 && (
          renderAccountTree(account.children, level + 1)
        )}
      </React.Fragment>
    ));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold mb-2">Chart of Accounts</h2>
          <p className="text-gray-600">Manage ledger accounts for double-entry accounting</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Account
        </button>
      </div>

      {/* Account Type Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {Object.entries(accountTypes).map(([type, label]) => (
          <div key={type} className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="text-sm text-gray-500">{label}</div>
            <div className="text-xl font-bold text-blue-600">{formatCurrency(totals[type] || 0)}</div>
          </div>
        ))}
      </div>

      {/* Accounts Table */}
      {accounts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <FolderIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>No accounts found. Click "Add Account" to create your first ledger account.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Opening Balance</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Balance</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderAccountTree(hierarchy)}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Account Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editingAccount ? 'Edit Account' : 'Add New Account'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Account Code *</label>
                <input
                  type="text"
                  value={formData.account_code}
                  onChange={(e) => setFormData({ ...formData, account_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., 1000, 2000, 3000"
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  disabled={!!editingAccount}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Name *</label>
                <input
                  type="text"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Account Type *</label>
                <select
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                  <option value="equity">Equity</option>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Parent Account</label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">None (Top Level)</option>
                  {accounts.filter(a => a.id !== editingAccount?.id).map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_code} - {account.account_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Opening Balance</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.opening_balance}
                  onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccounts;